/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import {
  EnterpriseViewerStatusService,
  IntegrationStatus
} from '@app/di/integrations/viewer/ev/enterprise-viewer-status.service';
import { filter, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Logger, MessageService } from '@core/lib/services';
import { DiConfigService } from '@app/di/di-config.service';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { HealthCheckType } from '@app/di/channel/window/healthcheck';
import { appConstants } from '@app/common/app-constants';
import { HealthStats, PerfStats, WindowStats } from '@app/di/channel/window/postmessage.types';
import { AppConfigService } from '@app/common/app-config.service';
import { UtilService } from '@app/util/util.service';
import { Events, EventService } from '@app/common/event';
import { FhirBuilderService } from '@app/di/channel/fhircast/model/fhir-builder.service';
import { FhirCastResource, FhirHubEvent } from '@app/di/channel/fhircast/model/fhircast-resource';
import { FhircastService } from '@app/di/channel/fhircast/fhircast.service';
import { FhircastTopicService } from '@app/di/channel/fhircast/fhircast-topic.service';
import { ApplicationConfig } from '@app/di/channel/websocket/websocket-registry';
import { IntegrationConfig } from '@app/di/data-model/di-fhircast-config';
import {
  PerformanceMonitorService,
  PerformanceStatus,
  PerformanceSource
} from '@app/ui/masthead/performance-monitor/performance-monitor.service';
import { Basic, OperationOutcome } from 'fhir/r4';
import { SignOutService } from '@app/common/sign-out.service';
import { CookieService } from 'ngx-cookie-service';
import { ModalsService } from '@app/common/modals/services/modals.service';

export enum UpdateResponse {
  HEALTH = 'health',
  WINDOW = 'window',
  PERFORMANCE = 'performance',
  SYNC_ERROR = 'syncError'
}

export enum FhirResponseType {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  TIMEOUT = 'TIMEOUT'
}

type SocketEvent = Event | MessageEvent | CloseEvent;

const log = new Logger('EnterpriseViewerService');

@Injectable({
  providedIn: 'root'
})
export class EnterpriseViewerService {
  private viewerWindow: Window = null;
  private evAppPath = '/genesis-viewer/';
  // model opened tracker to avoid multiple confirmation models
  private modalOpened = false;
  private unhealthyRelaunchAttempt = 0;
  private _topic: string;
  private openendContextsMap = new Map<number, string>();
  private lastEvent: {
    event: FhirHubEvent;
    reqId: string;
    inProgress: boolean;
    result: boolean;
    context?: any;
    isAutoAdvance?: boolean;
    id?: number;
    promise?: Promise<any>;
    resolve?: (value: any) => void;
    reject?: (reason?: any) => void;
    timer?: any;
  } = null;

  private integrationConfig: IntegrationConfig = null;

  constructor(
    private diConfigService: DiConfigService,
    private enterpriseViewerStatusService: EnterpriseViewerStatusService,
    private translateService: TranslateService,
    private modalsService: ModalsService,
    private postMessageService: PostMessengerService,
    private appConfigService: AppConfigService,
    private eventService: EventService,
    private fhirBuilder: FhirBuilderService,
    private fhirCastService: FhircastService,
    private util: UtilService,
    private messageService: MessageService,
    private signOutService: SignOutService,
    private cookieService: CookieService,
    private fhircastTopicService: FhircastTopicService
  ) {}

  public init() {
    this.integrationConfig = this.diConfigService.integrationConfig;
    this.createSubscription();
    this.afterEvents();
    this.setEvDateTimeFormat();

    this.fhircastTopicService.topicRenewed$
      .pipe(filter(sourceId => sourceId !== appConstants.APP_ID.GENESIS))
      .subscribe(() => {
        if (this.enterpriseViewerStatusService.getIntegrationStatus() === IntegrationStatus.FAILED) {
          return;
        }
        if (this.lastEvent?.timer) {
          clearTimeout(this.lastEvent.timer);
        }
        this.lastEvent = null;
        this.openendContextsMap.clear();
        this.enterpriseViewerStatusService.setLaunchedExamId(0);
        this.closeViewer();
        this.createSubscription();
      });

    this.signOutService.registerCallback(this.cleanup);
  }

  private createSubscription() {
    this._topic = this.fhircastTopicService.currentTopic;
    this.enterpriseViewerStatusService.systemStatusListening$.pipe(filter(Boolean), take(1)).subscribe(
      () => this.enterpriseViewerStatusService.viewerLoading(),
      _ => log.error('Listening if system status ready is failed')
    );
    const { Fhircast } = this.integrationConfig;
    const appConfig: ApplicationConfig = {
      id: appConstants.APP_ID.GENESIS,
      useSecureProtocol: Fhircast.WebSocketUseSecureProtocol,
      secureProtocolPrefix: Fhircast.WebSocketProtocolPrefix,
      secureProtocolTokenPrefix: Fhircast.WebSocketProtocolTokenPrefix,
      retryPeriod: Fhircast.RetryPeriod,
      maxRetry: Fhircast.RetryMaxcount,
      handlers: {
        onOpen: (event: Event) => this.socketEventHandler(event),
        onMessage: (event: MessageEvent) => this.socketEventHandler(event),
        onClose: (event: CloseEvent, isRetry) => this.socketEventHandler(event, isRetry)
      }
    };
    this.fhirCastService.register(this._topic, appConfig).catch(error => {
      log.error('Fhircast Registration:', `Topic: ${this._topic} `, 'Error: ', error);
      this.enterpriseViewerStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.error.connectionFailed',
        true
      );
      this.closeViewer();
    });
  }

  private socketEventHandler(event: SocketEvent, isRetry = false) {
    switch (event.type) {
      case 'open':
        log.info(`EV ↔ Hub WebSocket open: topic=${this._topic}`);
        this.enterpriseViewerStatusService.readyIntegrationStatus();
        if (this.viewerWindow && !this.viewerWindow.closed) {
          return;
        }
        this.launchViewer(this._topic);
        break;
      case 'message':
        const messageEvent: MessageEvent = <MessageEvent>event;
        const message = messageEvent.data && UtilService.deserializeString(messageEvent.data);
        log.info(`EV ← Hub Received: event=${message?.event?.['hub.event'] ?? 'unknown'} id=${message?.id ?? 'unknown'}`);
        this.handleEventMessage(message);
        break;
      case 'close':
        log.info(`EV ↔ Hub WebSocket close: topic=${this._topic} isRetry=${isRetry}`);
        if (isRetry) {
          this.enterpriseViewerStatusService.viewerLoading();
        } else {
          this.notifyUserRetry();
        }
        break;
    }
  }

  private handleEventMessage(message: any) {
    // Check if this is a response to our last request
    const { event } = message;
    if (event && event['hub.event']?.includes('sync')) {
      this.handleSyncResponse(message);
      return;
    }

    // Handle external close events from the viewer
    const { SYNC_SUCCESS, IMAGINGSTUDY_CLOSE } = FhirHubEvent;
    if (message?.id !== this.lastEvent?.reqId && message?.event?.['hub.event'] === IMAGINGSTUDY_CLOSE) {
      this.sendAcknowledgement(message.id);
      const tab = this.util.getActiveTab();
      if (tab && tab.id) {
        this.lastEvent = {
          event: IMAGINGSTUDY_CLOSE,
          reqId: message?.id,
          result: true,
          inProgress: false,
          id: tab?.workItem?.id
        };
        this.handleOpenedContexts(tab?.workItem, IMAGINGSTUDY_CLOSE);
        this.messageService.broadcast('lineShaft.viewerClosed', tab.id);
        const correlationId = this.openendContextsMap.get(tab?.workItem?.id);
        const fhirResource = this.fhirBuilder.build(
          null,
          this._topic,
          SYNC_SUCCESS,
          correlationId || null,
          IMAGINGSTUDY_CLOSE,
          message?.id
        );
        this.fhirCastService.publish(fhirResource);
      }
    }
  }

  private async launchViewer(topic: string) {
    const { Ev, Fhircast } = this.integrationConfig;
    // edge case for is genesis-viewer path is included or '//' is appended in path
    if (!Ev.Url.includes(this.evAppPath)) {
      Ev.Url = Ev.Url + this.evAppPath;
      Ev.Url = Ev.Url.replace(`/${this.evAppPath}`, this.evAppPath);
    }

    const urlParams = new URLSearchParams();
    urlParams.set('hub.topic', topic);
    urlParams.set('driveApp', appConstants.APP_ID.WFM);
    urlParams.set('correlationId', UtilService.getUUID());

    if (this.cookieService.get('serviceuserlogin') === 'true') {
      urlParams.set('serviceUser', 'true');
    }

    try {
      const token = await this.diConfigService.getJwtToken();
      if (token) {
        urlParams.set('accessToken', token);
      }
    } catch (e) {
      log.error('Failed to fetch JWT for viewer launch:', e);
    }

    const evUrl = `${Ev.Url}?${urlParams.toString()}`;
    const windowName = `EVWindow-${UtilService.getUniqueIdentifier()}`;
    this.viewerWindow = window.open(evUrl, windowName, 'popup');

    this.postMessageService.register({
      targetAppId: appConstants.APP_ID.GENESIS,
      targetAppWindow: this.viewerWindow,
      targetOrigin: Ev.Url,
      healthCheckInterval: Ev.EvHealthCheckInterval,
      healthCheckMaxRetry: Fhircast.RetryMaxcount,
      healthCheckType: HealthCheckType.PINGPONG,
      onWindowUpdates: response => this.updateHandler(response, UpdateResponse.WINDOW),
      onHealthUpdates: response => this.updateHandler(response, UpdateResponse.HEALTH)
    });
  }

  private updateHandler(response: HealthStats | WindowStats | PerfStats, type: UpdateResponse) {
    if (this.enterpriseViewerStatusService.logoutInProgress) {
      return;
    }

    if (type == UpdateResponse.HEALTH) {
      if ((<HealthStats>response).isHealthy) {
        this.unhealthyRelaunchAttempt = 0;
      } else {
        this.notifyUserRetry();
      }
    } else if (type == UpdateResponse.WINDOW) {
      if ((<WindowStats>response).isClosed) {
        this.openModal(type);
      }
    } else {
      log.warn('Performance Updates : Not Handeled', response);
    }
  }

  private notifyUserRetry() {
    if (this.unhealthyRelaunchAttempt == this.integrationConfig.Ev.EvLaunchRetryMaxCount) {
      this.unhealthyRelaunchAttempt = 0;
      this.closeViewer();
      this.enterpriseViewerStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.error.connectionFailed',
        true
      );
      return;
    }
    this.unhealthyRelaunchAttempt++;
    this.openModal(UpdateResponse.HEALTH);
  }

  private async openModal(modalType: UpdateResponse): Promise<'YES' | 'NO' | void> {
    if (this.modalOpened) {
      return;
    }
    this.modalOpened = true;
    const modelResponse = await this.modalsService.openEvErrorModal(modalType as any);
    this.modalOpened = false;
    if (modalType === UpdateResponse.SYNC_ERROR) {
      return modelResponse;
    }

    if (modelResponse === 'YES') {
      this.openendContextsMap.clear();
      this.enterpriseViewerStatusService.setLaunchedExamId(0);
      if (this.lastEvent?.timer) {
        clearTimeout(this.lastEvent.timer);
      }
      this.lastEvent = null;
      if (modalType === UpdateResponse.WINDOW) {
        this.postMessageService.unregister(appConstants.APP_ID.GENESIS);
        this.launchViewer(this._topic);
      } else {
        this.fhircastTopicService.renewTopic(appConstants.APP_ID.GENESIS);
        this.closeViewer();
        this.createSubscription();
      }
    } else {
      this.closeViewer();
      this.enterpriseViewerStatusService.failedIntegrationStatus(
        IntegrationStatus.FAILED,
        'fhir.integration.error.connectionFailed',
        false
      );
    }
    return modelResponse;
  }

  closeViewer() {
    this.postMessageService.unregister(appConstants.APP_ID.GENESIS, !this.viewerWindow?.closed);
    this.fhirCastService.unregister(appConstants.APP_ID.GENESIS);
  }

  /**
   * Sets the Enterprise Viewer (EV) date-time format preferences.
   * This method retrieves the date-time format configuration from the `DiConfigService`
   * it constructs a date-time preference object and updates the `AppConfigService`.
   * If the date-time formats are invalid or missing, the method logs a warning and exits.
   */
  private setEvDateTimeFormat() {
    const { Ev } = this.integrationConfig;
    if (!Ev.DateTimeFormat) {
      return;
    }
    const { TimeFormat } = Ev.DateTimeFormat;
    let { DateFormat, DateTimeFormat } = Ev.DateTimeFormat;
    const isValid =
      DateFormat &&
      DateTimeFormat &&
      TimeFormat &&
      UtilService.validateDateFormats(DateFormat, DateTimeFormat, TimeFormat);

    if (!isValid) {
      log.warn('Invalid DateTime Format For', DateFormat, DateTimeFormat, TimeFormat);
      return;
    }

    const dateFormatObj = {
      delimiter: null,
      M: '',
      D: '',
      Y: ''
    };
    for (let i = 0; i < DateFormat?.length; i++) {
      const character: string = DateFormat[i].toUpperCase();
      if ('MDY'.includes(character)) {
        dateFormatObj[character] += DateFormat[i];
      } else if (dateFormatObj.delimiter === null) {
        dateFormatObj.delimiter = DateFormat[i];
      }
    }
    const isMonthInDigit = appConstants.locale.monthInDigits.indexOf(this.translateService.currentLang) > -1;
    const inputFormat = DateFormat.replace(dateFormatObj['M'], 'MM');
    if (isMonthInDigit) {
      dateFormatObj['M'] = 'MM';
      DateFormat = DateFormat.replace(dateFormatObj['M'], 'MM');
      DateTimeFormat = DateTimeFormat.replace(dateFormatObj['M'], 'MM');
    }

    const dateTimePref = {
      dateDelimiter: dateFormatObj.delimiter,
      dateFormat: DateFormat,
      dateTimeFormat: DateTimeFormat,
      inputFormat: inputFormat,
      monthDayFormat: dateFormatObj['M'] + dateFormatObj.delimiter + dateFormatObj['D'],
      placeholderFormat: inputFormat,
      timeDateFormat: TimeFormat + ' ' + DateFormat,
      timeFormat: TimeFormat,
      yearFormat: dateFormatObj['Y']
    };
    this.appConfigService.setDateTimePref(dateTimePref);
  }

  private afterEvents() {
    [Events.read.name, Events.takeControl.name, Events.launchreadonly.name].forEach(event => {
      this.eventService.afterEvent(event, async (context: any) => {
        if (
          this.lastEvent?.event === FhirHubEvent.IMAGINGSTUDY_OPEN &&
          context?.id === this.lastEvent?.id &&
          (this.lastEvent?.inProgress || this.lastEvent?.result)
        ) {
          return;
        }

        if (context?.keys?.imageCount === 0) {
          return;
        }
        await this.open(context);
      });
    });
  }

  public home(isAutoAdvance = false) {
    return this.sendFhircastPublish(null, FhirHubEvent.HOME_OPEN, isAutoAdvance);
  }

  public closeAll() {
    return this.sendFhircastPublish(null, FhirHubEvent.CLOSE_ALL);
  }

  public close(context: any, isAutoAdvance = false) {
    return this.sendFhircastPublish(context, FhirHubEvent.IMAGINGSTUDY_CLOSE, isAutoAdvance);
  }

  public open(context: any) {
    if (context?.keys?.archiveFetchStatus !== appConstants.studyDataAvailability.ONLINE) {
      log.debug(`Aborting Event imagingstudy-open: Reason: ${context?.keys?.archiveFetchStatus} exam`);
      return true;
    }
    return this.sendFhircastPublish(context, FhirHubEvent.IMAGINGSTUDY_OPEN);
  }

  public logout() {
    return this.sendFhircastPublish(null, FhirHubEvent.USER_LOGOUT);
  }

  private logPerformance(event: FhirHubEvent, id: string | number, status: PerformanceStatus) {
    if (event === FhirHubEvent.IMAGINGSTUDY_OPEN) {
      PerformanceMonitorService.stop(PerformanceSource.VIEWER, id, status);
    }
  }

  private shoudSkipPublish(context: any, event: FhirHubEvent) {
    return !this.enterpriseViewerStatusService.isIntegrationReady() || this.isRedundantEvent(event, context?.id);
  }

  private async sendFhircastPublish(context: any, event: FhirHubEvent, isAutoAdvance = false, isRetry = false) {
    try {
      if (this.lastEvent?.inProgress) {
        const progressiveEvent = await this.handleInprogressEvent(context, event, isAutoAdvance);
        if (!progressiveEvent) return false;
      }

      if (this.shoudSkipPublish(context, event)) return true;
      PerformanceMonitorService.clear(PerformanceSource.VIEWER);
      const isBlockingEvent = this.isBlockingEvent(event);
      let correlationId = null;
      if (event === FhirHubEvent.IMAGINGSTUDY_OPEN) {
        correlationId = UtilService.getUUID();
        this.openendContextsMap.set(context.id, correlationId);
        PerformanceMonitorService.start(PerformanceSource.VIEWER, context.id);
      } else if (event === FhirHubEvent.IMAGINGSTUDY_CLOSE) {
        correlationId = this.openendContextsMap?.get(context.id);
      }

      const targetApp = event === FhirHubEvent.HOME_OPEN ? 'View' : undefined;
      const fhir: FhirCastResource = this.fhirBuilder.build(context, this._topic, event, correlationId, undefined, undefined, targetApp);
      log.debug(`Publishing Event: ${event} `, `: FHIR-Resource: ${fhir.id}`, UtilService.maskFhircastResource(fhir));
      this.enterpriseViewerStatusService.viewerInprogress();
      isBlockingEvent && UtilService.changeCursor('progress');
      const responsePromise = this.createResponsePromise(event, fhir.id, context, isAutoAdvance);
      await this.fhirCastService.publish(fhir, appConstants.APP_ID.GENESIS);
      this.sendAcknowledgement(this.lastEvent.reqId);
      if (event === FhirHubEvent.IMAGINGSTUDY_OPEN) {
        this.enterpriseViewerStatusService.setLaunchedExamId(context.id);
      }

      const syncResponse = await responsePromise;
      this.enterpriseViewerStatusService.readyIntegrationStatus();
      isBlockingEvent && UtilService.changeCursor('default');
      const msg = syncResponse === FhirResponseType.SUCCESS ? 'SyncSuccess' : 'SyncError';
      log.debug(
        `Published Event Response: ${msg} for Event: ${event} `,
        `: FHIR-Resource: ${fhir.id}`,
        UtilService.maskFhircastResource(fhir)
      );
      if (syncResponse === FhirResponseType.SUCCESS) {
        this.handleOpenedContexts(context, event);
        this.logPerformance(event, this.lastEvent.id, PerformanceStatus.SUCCESS);
        return true;
      }
      this.logPerformance(event, this.lastEvent.id, PerformanceStatus.ERROR);
      if (isRetry) {
        return false;
      }
      return await this.handleFalureStatus(IntegrationStatus.SYNC_ERROR, 'fhir.integration.error.syncError');
    } catch (error) {
      log.error(
        `Published Event Response: Error for Event: ${event} `,
        context ? `WorkItemID: ${context.id}` : '',
        error
      );
      if (this.lastEvent?.timer) {
        clearTimeout(this.lastEvent.timer);
        this.lastEvent = {
          ...this.lastEvent,
          inProgress: false,
          timer: null,
          promise: null,
          resolve: null,
          reject: null
        };
      }
      this.enterpriseViewerStatusService.setLaunchedExamId(0);
      this.logPerformance(event, this.lastEvent.id, PerformanceStatus.TIMEOUT);
      UtilService.changeCursor('default');
      if (isRetry) {
        return false;
      }
      return await this.handleFalureStatus(
        IntegrationStatus.LAST_ACTION_FAILED,
        'fhir.integration.error.connectionFailed'
      );
    }
  }

  async handleInprogressEvent(context: any, event: FhirHubEvent, isAutoAdvance = false): Promise<boolean> {
    if (!isAutoAdvance) {
      log.debug(`Blocking ${event}: Reason: Last Event: ${this.lastEvent?.event} in progress`);
      if (!this.modalOpened) this.enterpriseViewerStatusService.notifyViewerResponsePending();
      this.messageService.broadcast('clearActivatedItem');
      return false;
    }
    const isRedundant = this.isRedundantEvent(event, context?.id);
    if (isRedundant) {
      log.debug(`Skipping Block for ${event}: Reason: Last Event: ${this.lastEvent?.event} and ${event} is redundant`);
      return true;
    }

    // Wait for the last event to respond in autoadvace workflow
    await this.lastEvent.promise;
    // Unblocking autoadvance workflow and taken care in handleFalureStatus
    return true;
  }

  private async handleFalureStatus(integrationStatus: IntegrationStatus, message: string): Promise<boolean> {
    const { event, context, isAutoAdvance } = this.lastEvent;
    try {
      if (event === FhirHubEvent.USER_LOGOUT) {
        await UtilService.executeWithDelay(appConstants.TIME.SS_FIVE, () =>
          this.handleOpenedContexts(null, FhirHubEvent.USER_LOGOUT)
        );
        return true;
      }

      if (event === FhirHubEvent.HOME_OPEN) {
        this.messageService.broadcast('clearActivatedItem');
      }

      // silent close non active tab
      if (event === FhirHubEvent.IMAGINGSTUDY_CLOSE && context?.id !== this.util.getActiveTab()?.id) {
        return true;
      }

      const isBlockingEvent = this.isBlockingEvent(event);

      if (!isBlockingEvent) {
        this.enterpriseViewerStatusService.failedIntegrationStatus(integrationStatus, message, false);
        return false;
      }

      if (isAutoAdvance) {
        this.postMessageService.unregister(appConstants.APP_ID.GENESIS, !this.viewerWindow.closed);
        this.launchViewer(this._topic);
        return true;
      }

      if (integrationStatus === IntegrationStatus.SYNC_ERROR) {
        const publishResult = await this.sendFhircastPublish(context, event, isAutoAdvance, true);
        if (publishResult) {
          return true;
        }
      }

      const confirmation = await this.openModal(UpdateResponse.SYNC_ERROR);
      if (confirmation === 'YES') {
        this.postMessageService.unregister(appConstants.APP_ID.GENESIS, !this.viewerWindow.closed);
        this.launchViewer(this._topic);
        return true;
      }

      return false;
    } catch (error) {
      this.enterpriseViewerStatusService.failedIntegrationStatus(integrationStatus, message, false);
      log.error(
        `handleFailureStatus: event: ${event}, context: ${context?.id ?? 'none'}, autoAdvance: ${isAutoAdvance}`,
        error
      );
      return false;
    }
  }

  private isBlockingEvent(event: FhirHubEvent): boolean {
    return [FhirHubEvent.HOME_OPEN, FhirHubEvent.IMAGINGSTUDY_CLOSE, FhirHubEvent.CLOSE_ALL].includes(event);
  }

  private createResponsePromise(event: FhirHubEvent, reqId: string, context?: any, isAutoAdvance = false) {
    let pendingEvent;
    const promise = new Promise<FhirResponseType>((resolve, reject) => {
      pendingEvent = {
        event: event,
        reqId,
        result: false,
        context,
        isAutoAdvance,
        inProgress: true,
        id: context?.id,
        resolve,
        reject
      };

      this.lastEvent = pendingEvent;

      pendingEvent.timer = setTimeout(() => {
        log.warn(
          `Published Event Timeout:  for Event: ${event}`,
          `: FHIR-Resource: ${reqId}`,
          context ? `WorkItemID: ${context}` : ''
        );
        pendingEvent.inProgress = false;
        pendingEvent.timer = null;
        if (this.lastEvent?.reqId === reqId) {
          this.lastEvent = {
            ...this.lastEvent,
            inProgress: false,
            timer: null
          };
        }
        pendingEvent.reject?.(FhirResponseType.TIMEOUT);
      }, this.integrationConfig.Ev.EvRequestTimeout);
    });
    pendingEvent.promise = promise;
    this.lastEvent = pendingEvent;
    return promise;
  }

  private handleSyncResponse(message: FhirCastResource) {
    try {
      const { event } = message;
      const isSyncSuccess = event?.['hub.event'] === FhirHubEvent.SYNC_SUCCESS;
      let operationOutcome: OperationOutcome;
      let corelationResource: Basic;

      for (const context of event?.context || []) {
        if (context?.resource['resourceType'] === 'OperationOutcome') {
          operationOutcome = <OperationOutcome>context.resource;
        } else if (context?.resource['resourceType'] === 'Basic') {
          corelationResource = <Basic>context.resource;
        }
      }

      const responderAppId = this.getResponderAppId(operationOutcome, message);
      if (responderAppId && responderAppId !== appConstants.APP_ID.GENESIS) {
        log.debug(
          `Ignoring sync response for ${appConstants.APP_ID.GENESIS}: responder=${responderAppId} messageId=${message.id}`,
          `Last Event ID : ${this.lastEvent?.reqId} Event: ${this.lastEvent?.event}`
        );
        return;
      }

      const resourceCode = operationOutcome?.issue?.[0]?.details?.coding?.[0]?.code;

      if (resourceCode !== this.lastEvent?.reqId) {
        log.debug(
          `Skipping unrelated message for ID: ${message.id} Event: ${message?.event?.['hub.event']}`,
          `Last Event ID : ${this.lastEvent?.reqId} Event: ${this.lastEvent?.event}`
        );
        return;
      }

      if (resourceCode === this.lastEvent?.reqId) {
        this.lastEvent.result = isSyncSuccess;
        this.lastEvent.inProgress = false;
        const outcome = isSyncSuccess ? 'syncsuccess' : 'syncerror';
        log.info(`EV SyncResponse: ${outcome} matched for event=${this.lastEvent.event} reqId=${resourceCode}`);
        if (isSyncSuccess) {
          this.sendAcknowledgement(message.id, corelationResource?.identifier?.[0]?.value || '');
        }
        this.lastEvent?.resolve?.(isSyncSuccess ? FhirResponseType.SUCCESS : FhirResponseType.ERROR);
        if (this.lastEvent.timer) {
          clearTimeout(this.lastEvent.timer);
          this.lastEvent.timer = null;
        }
      }
    } catch (error) {
      log.error('HandleSyncResponse:', error);
    }
  }

  private getResponderAppId(operationOutcome?: OperationOutcome, message?: FhirCastResource): string | null {
    const responseAppId = operationOutcome?.extension?.find(extension => extension?.url === 'appId')?.valueString;
    const messageAppId = (message as any)?.appId || (message as any)?.applicationId;
    const normalizedAppId = responseAppId || messageAppId;
    return normalizedAppId ? normalizedAppId.toUpperCase() : null;
  }

  private sendAcknowledgement(id: string, correlationId?: string) {
    const ackMessage = {
      id,
      status: '200',
      ...(correlationId && { correlationId })
    };
    this.fhirCastService.sendNotification(appConstants.APP_ID.GENESIS, JSON.stringify(ackMessage));
  }

  private handleOpenedContexts(context: any, event: FhirHubEvent) {
    switch (event) {
      case FhirHubEvent.IMAGINGSTUDY_CLOSE:
        if (this.openendContextsMap.has(context?.id)) {
          this.openendContextsMap.delete(context?.id);
        }
        if (this.enterpriseViewerStatusService.getLaunchedExamId() === context.id) {
          this.enterpriseViewerStatusService.setLaunchedExamId(0);
        }
        break;
      case FhirHubEvent.CLOSE_ALL:
        this.openendContextsMap.clear();
        this.enterpriseViewerStatusService.setLaunchedExamId(0);
        break;
      case FhirHubEvent.USER_LOGOUT:
        this.openendContextsMap.clear();
        this.enterpriseViewerStatusService.logoutInProgress = true;
        this.enterpriseViewerStatusService.setLaunchedExamId(0);
        this.closeViewer();
    }
  }

  private isRedundantEvent(incomingEvent: FhirHubEvent, id?: number) {
    // Fast track for no context scenarios
    const noOpenContexts = !this.openendContextsMap.size;

    // Handle early cases without last event
    if (!this.lastEvent) {
      return incomingEvent === FhirHubEvent.HOME_OPEN;
    }

    // Duplicate workitem event check (high priority)
    if (this.lastEvent.id && this.lastEvent.id === id && this.lastEvent.event === incomingEvent) {
      return this.lastEvent.result;
    }

    // Event-specific redundancy rules
    switch (incomingEvent) {
      case FhirHubEvent.HOME_OPEN:
        return noOpenContexts || (this.lastEvent.event === FhirHubEvent.HOME_OPEN && this.lastEvent.result);

      case FhirHubEvent.CLOSE_ALL:
        return noOpenContexts;

      case FhirHubEvent.IMAGINGSTUDY_CLOSE:
        return noOpenContexts || !this.openendContextsMap.has(id) || this.lastEvent.event === FhirHubEvent.CLOSE_ALL
          ? this.lastEvent.result
          : false;

      default:
        return false;
    }
  }

  private cleanup = () => {
    this.fhirCastService.unregister(appConstants.APP_ID.GENESIS);
  };
}
